import React, { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  HoverVoice,
  playHoverSfx,
  sfxVolumeFromSettings,
} from "../utils/hoverSfx";

/**
 * Binds the procedural hover voices in `utils/hoverSfx` to DOM elements.
 *
 * Usage — spread the returned binder onto anything hoverable:
 *
 *   const hoverSfx = useHoverSfx();
 *   <button {...hoverSfx("topbar")} />
 *   <Link {...hoverSfx("bottombar", 2)} />   // seed 2 => third note of the run
 *
 * The seed is a stable index, so each element keeps its own pitch and a sweep
 * across a bar reads as a phrase instead of five identical clicks.
 */
const useHoverSfx = () => {
  const userSettings = useSelector((state: any) => state.userSettings);
  const volume = sfxVolumeFromSettings(userSettings);

  // Pointer can leave and re-enter across a child's border mid-gesture; only
  // re-trigger once it has genuinely left the element.
  const insideRef = useRef<EventTarget | null>(null);

  const play = useCallback(
    (voice: HoverVoice, seed: number, event: React.PointerEvent) => {
      // Touch has no hover — a tap would fire enter and click together.
      if (event.pointerType === "touch") return;
      if (insideRef.current === event.currentTarget) return;
      insideRef.current = event.currentTarget;
      playHoverSfx(voice, { volume, seed });
    },
    [volume]
  );

  const clear = useCallback((event: React.PointerEvent) => {
    if (insideRef.current === event.currentTarget) insideRef.current = null;
  }, []);

  return useMemo(
    () =>
      (voice: HoverVoice, seed = 0) => ({
        onPointerEnter: (event: React.PointerEvent) => play(voice, seed, event),
        onPointerLeave: clear,
      }),
    [play, clear]
  );
};

export default useHoverSfx;
